import { Button, Card, Switch } from "@repo/ui";
import React from "react";
import { Link } from "react-router-dom";

type NewsItem = {
  id: number;
  slug: string;
  title: string;
  description: string;
  created_at: string;
  created_by: string | null;
  published: boolean;
  category_id: number | null;
  category_name: string | null;
};

export default function AdminNews() {
  const [list, setList] = React.useState<NewsItem[]>([]);

  async function load() {
    const res = await fetch("/api/admin/news");
    const data = (await res.json()) as { status: string; data: NewsItem[] };
    setList(data.data || []);
  }

  React.useEffect(() => {
    void load();
  }, []);

  async function togglePublished(item: NewsItem, value: boolean) {
    await fetch(`/api/admin/news/${item.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ published: value }),
    });
    setList((prev) => prev.map((n) => (n.id === item.id ? { ...n, published: value } : n)));
  }

  async function remove(id: number) {
    if (!confirm("ลบข่าวนี้หรือไม่?")) return;
    await fetch(`/api/admin/news/${id}`, { method: "DELETE" });
    setList((prev) => prev.filter((n) => n.id !== id));
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl">ข่าวสาร</h1>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/admin/news-categories">จัดการหมวดหมู่</Link>
          </Button>
          <Button asChild>
            <Link to="/admin/news/new">สร้างข่าวใหม่</Link>
          </Button>
        </div>
      </div>
      <Card className="p-4 bg-card/60 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-foreground/70">
            <tr>
              <th className="py-2 pr-2">ชื่อข่าว</th>
              <th className="py-2 pr-2">หมวดหมู่</th>
              <th className="py-2 pr-2">สร้างเมื่อ</th>
              <th className="py-2 pr-2">ผู้เขียน</th>
              <th className="py-2 pr-2">สถานะ</th>
              <th className="py-2 pr-2 text-right">จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {list.map((n) => (
              <tr key={n.id} className="border-t border-border/60">
                <td className="py-2 pr-2">
                  <div className="font-medium">{n.title}</div>
                  <div className="text-xs text-foreground/60">/{n.slug}</div>
                </td>
                <td className="py-2 pr-2 text-foreground/70">
                  {n.category_name ? (
                    <span className="inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                      {n.category_name}
                    </span>
                  ) : (
                    <span className="text-xs text-foreground/50">ไม่ระบุ</span>
                  )}
                </td>
                <td className="py-2 pr-2 text-foreground/70">
                  {new Date(n.created_at).toLocaleString("th-TH")}
                </td>
                <td className="py-2 pr-2 text-foreground/70">{n.created_by || "-"}</td>
                <td className="py-2 pr-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={n.published}
                      onCheckedChange={(v) => void togglePublished(n, Boolean(v))}
                    />
                    <span className="text-xs text-foreground/70">
                      {n.published ? "เผยแพร่" : "ฉบับร่าง"}
                    </span>
                  </div>
                </td>
                <td className="py-2 pr-2 text-right">
                  <div className="inline-flex gap-2">
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/admin/news/${n.slug}/edit`}>แก้ไข</Link>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-destructive border-destructive/60"
                      onClick={() => void remove(n.id)}
                    >
                      ลบ
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td className="py-4 text-center text-foreground/60" colSpan={5}>
                  ยังไม่มีข่าว
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
